import { filterEmptyValues } from '@marketplace-web/shared/utils'

import { CATALOG_URL } from 'constants/routes'

import {
  BRAND_ROUTE_REGEX,
  CATALOG_BRAND_ROUTE,
  CATALOG_MAIN_ROUTE,
  CATALOG_PER_PAGE,
  CATALOG_ROUTE_REGEX,
  CatalogPhotoThumbnail,
  NONE_CATALOG_ROUTE,
} from '../constants/catalog'
import { Pagination } from '../types/catalog-provider'
import { FilterModel } from '../types/models/filter'
import { SelectedDynamicFilterModel } from '../types/models/dynamic-filter'
import { CatalogPhotoDto } from '../types/dtos/catalog'
import { CatalogItem } from '../types/models/catalog-item'
import { CatalogNavigationModel } from '../types/models/catalog'

import {
  buildFilterUrlParams,
  CatalogExtraUrlParams,
  getSelectedDynamicFiltersParams,
} from './catalog-filter'

type BuildCatalogUrlParamsArgs = {
  extraParams?: CatalogExtraUrlParams
  filters: Partial<FilterModel>
  selectedDynamicFilters?: Array<SelectedDynamicFilterModel>
}

export const getCatalogPhotoSrc = (photo: CatalogPhotoDto) => {
  const targetSrc = photo.thumbnails.find(({ type }) => type === CatalogPhotoThumbnail.Thumb24)
  const retinaSrc = photo.thumbnails.find(({ type }) => type === CatalogPhotoThumbnail.Thumb48)
  const src = targetSrc?.url || photo.url

  if (!targetSrc || !retinaSrc) return { src }

  return {
    src,
    srcset: `${src} 1x, ${retinaSrc.url} 2x`,
  }
}

export const buildCatalogUrlParams = ({
  filters,
  selectedDynamicFilters,
  extraParams,
}: BuildCatalogUrlParamsArgs) => {
  const filterParams = buildFilterUrlParams(filters)
  const dynamicParams = getSelectedDynamicFiltersParams(selectedDynamicFilters)

  const extraDynamicParamKeys = Object.keys(extraParams || {}).filter(key => key.endsWith('_ids'))
  const dynamicParamKeys = Object.keys(dynamicParams)
  const paramsToRemove = {}

  extraDynamicParamKeys.forEach(key => {
    if (!dynamicParamKeys.includes(key)) {
      paramsToRemove[key] = null
    }
  })

  const params = {
    ...extraParams,
    ...filterParams,
    ...dynamicParams,
    ...paramsToRemove,
  }

  return filterEmptyValues(params)
}

export const buildCatalogPaginationParams = (pagination: Pagination) => {
  const page = Number(pagination.page) > 1 ? pagination.page : null
  const perPage = pagination.perPage === CATALOG_PER_PAGE ? null : pagination.perPage

  return {
    per_page: perPage,
    time: pagination.time,
    page,
  }
}

export const getCatalogInitializersParamsFromUrl = (
  relativeUrl: string,
  params: Record<string, string | Array<string> | undefined>,
): Record<string, string | Array<string> | undefined> => {
  if (relativeUrl === CATALOG_URL) {
    // catalog?color_ids[]=1&catalog[]=2050
    return params
  }

  if (relativeUrl.match(CATALOG_ROUTE_REGEX)) {
    // catalog/5-men
    const catalogIdMatches = relativeUrl.match(CATALOG_ROUTE_REGEX)
    const catalogId = catalogIdMatches?.[1]

    return {
      ...params,
      catalog: catalogId && [catalogId],
    }
  }

  if (relativeUrl.match(BRAND_ROUTE_REGEX)) {
    // brand/175586-garancia
    const brandIdMatches = relativeUrl.match(BRAND_ROUTE_REGEX)
    const brandId = brandIdMatches?.[1]

    return {
      ...params,
      brand_ids: brandId && [brandId],
    }
  }

  return params
}

export const getIsInCatalog = (relativeUrl: string): boolean => {
  if (relativeUrl === NONE_CATALOG_ROUTE) return false

  return relativeUrl.startsWith(CATALOG_MAIN_ROUTE) || relativeUrl.startsWith(CATALOG_BRAND_ROUTE)
}

export const catalogsNavigationsInAscOrder = (
  catalog: CatalogNavigationModel,
  other: CatalogNavigationModel,
) => catalog.order - other.order

export const catalogModelToCatalogListItem = (catalog: CatalogNavigationModel): CatalogItem => {
  const { id, title, catalogIds, parentId, code, depth, photo, badge } = catalog

  return {
    id,
    title,
    value: id,
    data: {
      id,
      catalogIds,
      parentId,
      code,
      depth,
      photo,
      badge,
    },
  }
}

export const catalogModelsToCatalogListItems = (
  catalogs: Array<CatalogNavigationModel>,
): Array<CatalogItem> => catalogs.map(catalogModelToCatalogListItem)

export const getNewPriceRange = (
  priceFrom: string | null | undefined,
  priceTo: string | null | undefined,
) => {
  if (priceFrom && priceTo && parseFloat(priceFrom) > parseFloat(priceTo)) {
    return { priceFrom: priceTo, priceTo: null }
  }

  return { priceTo, priceFrom }
}
